import { useEffect, useMemo, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Mail, Wand2, Check, Pencil, Send, Loader2, Search, MousePointerClick, History } from "lucide-react";
import EmailBuilder from "@/components/EmailBuilder";

/**
 * Admin Broadcast Email composer.
 *
 * Lets admins:
 *  1. Compose an email with the same Unlayer drag-and-drop builder as Special Offers
 *  2. Filter / search the full user list (businesses + customers)
 *  3. Checkmark exactly who to email
 *  4. Send.
 *
 * Used for one-off welcome-style blasts, announcement emails, etc.
 */
export default function AdminBroadcastEmailTab({ users, loading }) {
  const [subject, setSubject] = useState("");
  const [previewText, setPreviewText] = useState("");
  const [design, setDesign] = useState(null);
  const [html, setHtml] = useState(null);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [filterRole, setFilterRole] = useState("all"); // all | business | customer
  const [filterQ, setFilterQ] = useState("");
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState({ items: [], totals: null });

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/broadcast-history");
      setHistory(data);
    } catch (e) {
      console.warn("broadcast history load failed:", e);
    }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const visible = useMemo(() => {
    const q = filterQ.trim().toLowerCase();
    return (users || []).filter((u) => {
      if (filterRole !== "all" && u.role !== filterRole) return false;
      if (!q) return true;
      const haystack = [
        u.name,
        u.business_name,
        u.email,
        u.city,
        u.business_city,
      ].filter(Boolean).join(" ").toLowerCase();
      return haystack.includes(q);
    });
  }, [users, filterRole, filterQ]);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const selectAllVisible = () => {
    const next = new Set(selected);
    visible.forEach((u) => u.email && next.add(u.id));
    setSelected(next);
  };

  const clearSelection = () => setSelected(new Set());

  const send = async () => {
    if (!subject.trim()) return toast.error("Subject is required");
    if (!html) return toast.error("Design the email body in the builder first");
    if (selected.size === 0) return toast.error("Select at least one recipient");
    setSending(true);
    try {
      const { data } = await api.post("/admin/broadcast-email", {
        subject: subject.trim(),
        html_body: html,
        design_json: design,
        target_user_ids: Array.from(selected),
        preview_text: previewText.trim(),
      });
      toast.success(`Queued ${data.sent} email${data.sent === 1 ? "" : "s"}. ${data.skipped_no_email ? `${data.skipped_no_email} skipped (no email on file).` : ""}`);
      // Reset compose state but keep selection so admin can iterate
      setSubject("");
      setPreviewText("");
      setDesign(null);
      setHtml(null);
      setBuilderOpen(false);
      loadHistory();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSending(false);
    }
  };

  const counts = useMemo(() => {
    let withEmail = 0;
    let noEmail = 0;
    selected.forEach((id) => {
      const u = users.find((x) => x.id === id);
      if (u && u.email) withEmail += 1;
      else if (u) noEmail += 1;
    });
    return { withEmail, noEmail };
  }, [selected, users]);

  return (
    <div className="mt-3 space-y-5" data-testid="admin-broadcast-tab">
      <Card className="rounded-sm border border-border p-5 space-y-4">
        <div className="flex items-center gap-2 flex-wrap">
          <Mail size={16} className="text-[#B31E1E]" />
          <h3 className="font-display text-xl">Compose broadcast email</h3>
          <Badge variant="outline" className="rounded-sm text-[10px] font-mono">
            {selected.size} recipient{selected.size === 1 ? "" : "s"} selected
          </Badge>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="bc-subject">Subject</Label>
            <Input
              id="bc-subject"
              value={subject}
              maxLength={200}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Welcome to Servrevu — claim your free profile"
              className="rounded-md mt-1"
              data-testid="broadcast-subject-input"
            />
          </div>
          <div>
            <Label htmlFor="bc-preview">Preview text <span className="text-gray-400 font-normal">(optional)</span></Label>
            <Input
              id="bc-preview"
              value={previewText}
              maxLength={200}
              onChange={(e) => setPreviewText(e.target.value)}
              placeholder="A short teaser shown in inbox previews."
              className="rounded-md mt-1"
              data-testid="broadcast-preview-input"
            />
          </div>
        </div>

        {/* Builder */}
        {!builderOpen && !html && (
          <div className="border border-dashed border-border rounded-md p-6 text-center bg-white">
            <Wand2 size={22} className="mx-auto text-[#B31E1E]" />
            <p className="mt-2 text-sm text-gray-700">Drag-and-drop blocks (images, headers, buttons, CTAs) to design the email body.</p>
            <Button type="button" onClick={() => setBuilderOpen(true)} className="mt-3 rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717] shadow-sm hover:shadow-md" data-testid="broadcast-builder-open">
              <Wand2 size={13} className="mr-1.5" /> Open email builder
            </Button>
          </div>
        )}

        {!builderOpen && html && (
          <div className="border border-emerald-300 bg-emerald-50 rounded-md p-4 flex items-start justify-between gap-3 flex-wrap" data-testid="broadcast-design-saved">
            <div className="flex items-start gap-2">
              <Check size={16} className="text-emerald-700 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-emerald-900">Email design saved</p>
                <p className="text-xs text-emerald-800 mt-0.5">{(html.length / 1024).toFixed(1)} KB — ready to send.</p>
              </div>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={() => setBuilderOpen(true)} className="rounded-[35px]" data-testid="broadcast-builder-edit">
              <Pencil size={12} className="mr-1.5" /> Edit
            </Button>
          </div>
        )}

        {builderOpen && (
          <EmailBuilder
            businessName="Servrevu"
            initialDesign={design}
            onSave={(d, h) => { setDesign(d); setHtml(h); setBuilderOpen(false); toast.success("Design saved"); }}
            onCancel={() => setBuilderOpen(false)}
          />
        )}

        <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-border">
          <Button
            type="button"
            disabled={sending || !html || !subject || selected.size === 0}
            onClick={send}
            className="rounded-[35px] shadow-sm hover:shadow-md bg-[#B31E1E] hover:bg-[#8F1717]"
            data-testid="broadcast-send-button"
          >
            {sending ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Sending…</> : <><Send size={14} className="mr-1.5" /> Send to {counts.withEmail} recipient{counts.withEmail === 1 ? "" : "s"}</>}
          </Button>
          {counts.noEmail > 0 && (
            <span className="text-xs font-mono text-amber-700">
              {counts.noEmail} selected user{counts.noEmail === 1 ? "" : "s"} have no email on file and will be skipped.
            </span>
          )}
        </div>
      </Card>

      {/* Recipient selector */}
      <Card className="rounded-sm border border-border p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div className="flex items-center gap-2">
            <h3 className="font-display text-xl">Recipients</h3>
            <Badge variant="outline" className="rounded-sm text-[10px] font-mono">
              {visible.length} visible · {selected.size} selected
            </Badge>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={selectAllVisible}
              className="text-xs font-mono text-[#B31E1E] hover:underline"
              data-testid="broadcast-select-all-visible"
            >
              Select all visible
            </button>
            <span className="text-gray-300">·</span>
            <button
              type="button"
              onClick={clearSelection}
              className="text-xs font-mono text-gray-500 hover:underline"
              data-testid="broadcast-clear-selection"
            >
              Clear ({selected.size})
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3 mb-3">
          <div className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              value={filterQ}
              onChange={(e) => setFilterQ(e.target.value)}
              placeholder="Search name, email, business name, city…"
              className="rounded-md pl-9"
              data-testid="broadcast-filter-input"
            />
          </div>
          <div className="flex gap-1.5">
            {["all", "business", "customer"].map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => setFilterRole(r)}
                className={`px-3 py-1.5 rounded-[35px] text-xs font-mono border transition-colors ${
                  filterRole === r
                    ? "bg-[#B31E1E] text-white border-[#B31E1E]"
                    : "bg-white text-gray-700 border-border hover:bg-gray-50"
                }`}
                data-testid={`broadcast-filter-role-${r}`}
              >
                {r === "all" ? "All" : r === "business" ? "Businesses" : "Customers"}
              </button>
            ))}
          </div>
        </div>

        {loading && (!users || users.length === 0) ? (
          <p className="text-sm text-gray-500">Loading users…</p>
        ) : visible.length === 0 ? (
          <p className="text-sm text-gray-500" data-testid="broadcast-no-users">No users match your filters.</p>
        ) : (
          <div className="border border-border rounded-md overflow-hidden max-h-[440px] overflow-y-auto">
            <ul className="divide-y divide-border" data-testid="broadcast-user-list">
              {visible.map((u) => {
                const checked = selected.has(u.id);
                const displayName = u.business_name || u.name || "(no name)";
                const hasEmail = !!u.email;
                return (
                  <li key={u.id} className={`flex items-center gap-3 px-3 py-2 ${checked ? "bg-[#B31E1E]/5" : "hover:bg-gray-50"}`} data-testid={`broadcast-user-row-${u.id}`}>
                    <Checkbox
                      checked={checked}
                      onCheckedChange={() => toggle(u.id)}
                      disabled={!hasEmail}
                      data-testid={`broadcast-user-check-${u.id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate" title={displayName}>{displayName}</p>
                      <p className="text-[11px] font-mono text-gray-500 truncate">
                        {u.email || <span className="text-amber-700">no email on file</span>}
                        {(u.city || u.business_city) && <span> · {u.business_city || u.city}{(u.state || u.business_state) ? `, ${u.business_state || u.state}` : ""}</span>}
                      </p>
                    </div>
                    <Badge
                      variant="outline"
                      className={`rounded-sm text-[10px] font-mono ${u.role === "business" ? "border-[#B31E1E] text-[#B31E1E]" : "border-emerald-400 text-emerald-700"}`}
                    >
                      {u.role}
                    </Badge>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </Card>

      {/* Broadcast history + engagement */}
      <BroadcastHistoryCard history={history} onRefresh={loadHistory} />
    </div>
  );
}

function BroadcastHistoryCard({ history, onRefresh }) {
  const items = history?.items || [];
  const totals = history?.totals;

  const formatTs = (ts) => {
    if (!ts) return "—";
    try { return new Date(ts).toLocaleString(); } catch { return ts; }
  };

  return (
    <Card className="rounded-sm border border-border p-5" data-testid="broadcast-history-card">
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <History size={16} className="text-[#B31E1E]" />
        <h3 className="font-display text-xl">Broadcast history</h3>
        <Badge variant="outline" className="rounded-sm text-[10px] font-mono">
          {items.length} sent
        </Badge>
        <button
          type="button"
          onClick={onRefresh}
          className="ml-auto text-xs font-mono text-[#B31E1E] hover:underline"
          data-testid="broadcast-history-refresh"
        >
          Refresh
        </button>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4" data-testid="broadcast-history-totals">
        <Card className="rounded-sm border border-border p-3">
          <div className="text-[10px] font-mono uppercase tracking-wider text-gray-500">Broadcasts</div>
          <p className="font-display text-2xl mt-1 tabular-nums">{totals?.broadcasts ?? 0}</p>
        </Card>
        <Card className="rounded-sm border border-border p-3">
          <div className="text-[10px] font-mono uppercase tracking-wider text-gray-500">Sent</div>
          <p className="font-display text-2xl mt-1 tabular-nums">{totals?.sent ?? 0}</p>
        </Card>
        <Card className="rounded-sm border border-border p-3">
          <div className="text-[10px] font-mono uppercase tracking-wider text-gray-500">Delivered</div>
          <p className="font-display text-2xl mt-1 tabular-nums text-emerald-700">{totals?.delivered ?? 0}</p>
        </Card>
        <Card className="rounded-sm border border-border p-3">
          <div className="text-[10px] font-mono uppercase tracking-wider text-gray-500">Opens</div>
          <p className="font-display text-2xl mt-1 tabular-nums text-[#B31E1E]">{totals?.opens ?? 0}</p>
        </Card>
        <Card className="rounded-sm border border-border p-3">
          <div className="text-[10px] font-mono uppercase tracking-wider text-gray-500">Clicks</div>
          <p className="font-display text-2xl mt-1 tabular-nums text-fuchsia-700">{totals?.clicks ?? 0}</p>
        </Card>
      </div>

      {items.length === 0 ? (
        <div className="border border-dashed border-border rounded-md p-6 text-center" data-testid="broadcast-history-empty">
          <p className="text-sm text-gray-600">No broadcasts sent yet. Compose one above to get started.</p>
        </div>
      ) : (
        <div className="border border-border rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr className="text-left">
                <th className="px-3 py-2 text-[11px] font-mono uppercase tracking-wider">Subject</th>
                <th className="px-3 py-2 text-[11px] font-mono uppercase tracking-wider">Sent</th>
                <th className="px-3 py-2 text-[11px] font-mono uppercase tracking-wider text-right">Recipients</th>
                <th className="px-3 py-2 text-[11px] font-mono uppercase tracking-wider text-right">Delivered</th>
                <th className="px-3 py-2 text-[11px] font-mono uppercase tracking-wider text-right">Opens</th>
                <th className="px-3 py-2 text-[11px] font-mono uppercase tracking-wider text-right">Open%</th>
                <th className="px-3 py-2 text-[11px] font-mono uppercase tracking-wider text-right">Clicks</th>
                <th className="px-3 py-2 text-[11px] font-mono uppercase tracking-wider text-right">Click%</th>
                <th className="px-3 py-2 text-[11px] font-mono uppercase tracking-wider">Last click</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {items.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50" data-testid={`broadcast-history-row-${r.id}`}>
                  <td className="px-3 py-2 font-medium text-gray-900 max-w-[260px] truncate" title={r.subject}>
                    {r.subject || "(untitled)"}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-gray-600 whitespace-nowrap">
                    {formatTs(r.created_at)}
                  </td>
                  <td className="px-3 py-2 tabular-nums text-right">{r.sent}</td>
                  <td className="px-3 py-2 tabular-nums text-right text-emerald-700">{r.delivered}</td>
                  <td className="px-3 py-2 tabular-nums text-right text-[#B31E1E]">{r.opens}</td>
                  <td className="px-3 py-2 tabular-nums text-right">{r.open_rate_pct}%</td>
                  <td className="px-3 py-2 tabular-nums text-right">
                    {r.clicks > 0 ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200 px-2 py-0.5 text-xs font-semibold">
                        <MousePointerClick size={10} /> {r.clicks}
                      </span>
                    ) : (
                      <span className="text-gray-400">0</span>
                    )}
                  </td>
                  <td className="px-3 py-2 tabular-nums text-right">{r.click_rate_pct}%</td>
                  <td className="px-3 py-2 font-mono text-xs text-gray-600 whitespace-nowrap">{formatTs(r.last_click_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-3 text-[11px] font-mono text-gray-400">
        Opens &amp; delivered come from Resend webhook events. Clicks are tracked through the wrapped CTA links inside the broadcast HTML.
      </p>
    </Card>
  );
}
